/**
 * Session-only turn status storage — ack/thinking are NOT persisted in DB.
 * Ack text is typewritten on the client after ack_done; saved here for session reloads.
 * keyed by clientTurnId → message_id for in-session reloads.
 */

const STORAGE_KEY = 'reruption_turn_status';

export function createClientTurnId() {
  return `turn_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function readStore() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function writeStore(store) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // sessionStorage full or blocked — non-fatal
  }
}

export function persistTurnStatus({ clientTurnId, messageId, ackText, thinkingText }) {
  if (!ackText && !thinkingText) return;
  const store = readStore();
  const payload = {
    ackText: ackText || null,
    thinkingText: thinkingText || null,
    updatedAt: Date.now(),
  };
  if (clientTurnId) store[clientTurnId] = payload;
  if (messageId) {
    store[messageId] = payload;
    if (clientTurnId) delete store[clientTurnId];
  }
  writeStore(store);
}

export function getStoredTurnStatus(message) {
  if (!message) return null;
  const store = readStore();
  const keys = [message.message_id, message.clientTurnId].filter(Boolean);
  for (const key of keys) {
    if (store[key]?.ackText) return store[key];
  }
  return null;
}

export function mergeTurnStatusIntoMessage(message) {
  if (!message || message.role !== 'assistant') return message;
  if (message.ackText) return message;
  const stored = getStoredTurnStatus(message);
  if (!stored?.ackText) return message;
  return { ...message, ackText: stored.ackText };
}

export function mergeTurnStatusIntoMessages(messages) {
  if (!Array.isArray(messages)) return messages;
  return messages.map(mergeTurnStatusIntoMessage);
}

/** Empty assistant placeholder — ack typewrites in after ack_done SSE. */
export function createAssistantPlaceholder(userMessage, lang, extraFields = {}) {
  const clientTurnId = createClientTurnId();
  return {
    role: 'assistant',
    content: '',
    isStreaming: true,
    ackText: '',
    ackStreaming: true,
    thinkingText: null,
    thinkingVerbs: null,
    clientTurnId,
    ...extraFields,
  };
}
